import { nextTick } from "vue";
import { useWorkspace } from "../Stores/Workspaces";
import { useAppState } from "../Stores/AppState";
import { useWorkspaceTree } from "../Stores/WorkspaceTree";

/**
 * LocationAnimation utility class for handling location-based animations
 * and scrolling to specific elements in the workspace
 */
export class LocationAnimation {
    constructor() {
        this.workspaceStore = useWorkspace();
        this.workspaceTreeStore = useWorkspaceTree();
        this.appStateStore = useAppState();
    }

    /**
     * Sets the locate class name for animation targeting
     * @param {string} type - The type of element (space, board, item, group)
     * @param {Object} item - The item object to locate
     */
    setLocateClassName(type, item) {
        const resolvedItem = (() => {
            switch (type) {
                case "space":
                    return item || this.workspaceTreeStore.activeWorkspace;
                case "board":
                    return item || this.workspaceTreeStore.activeBoard;
                case "item":
                    return item || this.workspaceTreeStore.activeItem;
                case "group":
                    return item; // For groups, item is already the group object
                default:
                    return item;
            }
        })();

        this.workspaceStore.active_item_locator = null;
        this.workspaceStore.active_item_locator_type = type;
        this.workspaceStore.active_item_locator_temp = `auto-scroll-${resolvedItem?.id}${resolvedItem?.slug}`;
    }

    /**
     * Redirects to active item with scroll animation
     */
    redirectToActiveItem() {
        const activeItem = this.workspaceTreeStore.activeItem;
        if (!activeItem) return;

        const elId = `auto-scroll-${activeItem?.id}${activeItem?.slug}`;
        const element = document?.getElementsByClassName(elId)?.[0];

        nextTick(() => {
            if (!this.workspaceStore.activeItemSavedScrollPosition && element) {
                element.scrollIntoView?.({
                    block: "end",
                });
            } else if (element) {
                const categoryElement = element.closest(
                    ".propus-items-container",
                );
                const categoriesElement = element.closest(
                    ".prospus-draggable-categories",
                );
                const { item, category } =
                    this.workspaceStore.activeItemSavedScrollPosition;
                categoriesElement.scrollBy(category.x, category.y);
                categoryElement.scrollBy(item.x, item.y);
                this.workspaceStore.activeItemSavedScrollPosition = null;
            }

            this.setActiveItemLocator();
        });
    }

    /**
     * Redirects to active space with scroll animation
     */
    redirectToActiveSpace() {
        const activeSpace = this.workspaceTreeStore.activeWorkspace;
        if (!activeSpace) return;

        const elId = "id-" + activeSpace?.id + activeSpace?.slug;

        if (elId) {
            nextTick(() => {
                const ws = this.workspaceTreeStore.getWorkspaceById(
                    activeSpace.id,
                );
                const activeSpaceColumn = ws
                    ? this.workspaceTreeStore.getWorkspaceCategoryById(
                          ws.workspaceCategoryId,
                      )
                    : null;
                const scrollContainer = document.querySelector(
                    "#prospus-workpanel-spaces-container",
                );

                if (!scrollContainer) return;

                let activeSpaceOrder = activeSpaceColumn?.order;
                if (
                    activeSpaceOrder === null ||
                    activeSpaceOrder === undefined
                ) {
                    const catId = ws?.workspaceCategoryId;
                    activeSpaceOrder =
                        catId != null
                            ? this.workspaceTreeStore.workspaceCategoryOrderedIds.indexOf(
                                  catId,
                              )
                            : -1;
                }

                const totalColumns = scrollContainer.children.length;
                const containerWidth = scrollContainer.offsetWidth;
                const columnWidth = scrollContainer.children[0].offsetWidth;
                const availableColumns = totalColumns - activeSpaceOrder;

                const visibleColumnsCount = Math.trunc(
                    containerWidth / columnWidth,
                );
                const isSpaceOrderLessThanVisibleColumns =
                    activeSpaceOrder < visibleColumnsCount;

                const isSpaceOrderLessThanAvailableColumns =
                    activeSpaceOrder < availableColumns;

                const isSpaceVisible =
                    containerWidth > availableColumns * columnWidth * 0.5;

                const canBeShownNearest =
                    isSpaceVisible ||
                    isSpaceOrderLessThanVisibleColumns ||
                    isSpaceOrderLessThanAvailableColumns;

                document.getElementById(elId)?.scrollIntoView?.({
                    block: "end",
                    inline: canBeShownNearest ? "nearest" : "start",
                });

                this.setActiveItemLocator();
                setTimeout(() => {
                    if (this.workspaceStore.active_item_locator_temp) {
                        this.workspaceStore.active_item_locator =
                            this.workspaceStore.active_item_locator_temp;
                        this.workspaceStore.active_item_locator_temp = null;
                    }
                }, 1200);
            });
        }
    }

    /**
     * Redirects to active board with scroll animation
     */
    redirectToActiveBoard() {
        const activeBoard = this.workspaceTreeStore.activeBoard;
        if (!activeBoard) return;
        const elId = `auto-scroll-${activeBoard?.id}${activeBoard?.slug}`;
        this.redirectToActive(elId);
    }

    /**
     * Redirects to active board group with scroll animation
     */
    redirectToActiveBoardGroup() {
        const activeBoardCategory = this.workspaceTreeStore.activeBoardCategory;
        if (!activeBoardCategory) return;
        this.workspaceStore.openBoardsPanel();
        const elId = `auto-scroll-${activeBoardCategory?.id}${activeBoardCategory?.slug}`;
        this.redirectToActive(elId);
    }

    /**
     * Redirects to active space group with scroll animation
     */
    redirectToActiveSpaceGroup() {
        const activeSpaceCategory =
            this.workspaceTreeStore.activeWorkspaceCategory;
        if (!activeSpaceCategory) return;
        this.workspaceStore.openSpacesPanel();
        const elId = `auto-scroll-${activeSpaceCategory?.id}${activeSpaceCategory?.slug}`;
        this.redirectToActive(elId);
    }

    /**
     * Generic redirect to active element with scroll animation
     * @param {string} id - The element ID to scroll to
     */
    redirectToActive(id = this.workspaceStore.active_item_locator_temp) {
        const elem = document.querySelector(`.${id}`);
        elem?.scrollIntoView?.({ block: "end" });
        this.setActiveItemLocator();
    }

    /**
     * Sets the active item locator with timeout
     */
    setActiveItemLocator() {
        setTimeout(() => {
            if (this.workspaceStore.active_item_locator_temp) {
                this.workspaceStore.active_item_locator =
                    this.workspaceStore.active_item_locator_temp;
                this.workspaceStore.active_item_locator_temp = null;
            }
        }, 1200);
    }

    /**
     * Checks if an element is in the viewport
     * @param {HTMLElement} element - The element to check
     * @returns {boolean} - True if element is in viewport
     */
    isElementInViewport(element) {
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <=
                (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <=
                (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    /**
     * Scrolls to a specific element by ID with animation
     * @param {string} elementId - The ID of the element to scroll to
     * @param {Object} options - Scroll options (block, inline, etc.)
     */
    scrollToElement(elementId, options = { block: "end" }) {
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollIntoView?.(options);
        }
    }

    /**
     * Scrolls to a specific element by class name with animation
     * @param {string} className - The class name of the element to scroll to
     * @param {Object} options - Scroll options (block, inline, etc.)
     */
    scrollToElementByClass(className, options = { block: "end" }) {
        const element = document.querySelector(`.${className}`);
        if (element) {
            element.scrollIntoView?.(options);
        }
    }

    /**
     * Navigates to an item with full business logic
     * @param {number} itemId - The item ID to navigate to
     * @param {number} spaceId - The space ID containing the item
     * @param {Object} itemData - The item data object for animation
     * @param {Function} openConversationMappedItem - Optional callback for opening conversation mapped item
     */
    async navigateToItem(
        itemId,
        spaceId,
        itemData = null,
        openConversationMappedItem = null,
        boardId = null,
    ) {
        if (openConversationMappedItem) {
            openConversationMappedItem();
            return;
        }

        this.appStateStore.is_dashboard_mode = false;
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        if (!space?.boardCategoryOrderedIds?.length) {
            await this.workspaceTreeStore.loadBoards(space);
        }
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        const board = this.workspaceTreeStore.getBoardById(boardId);
        if (!board?.itemCategoryOrderedIds?.length) {
            await this.workspaceTreeStore.loadItems(space, board);
        }
        this.workspaceTreeStore.setActiveBoardId(board?.id, true);
        const itemRow = this.workspaceTreeStore.getItemById(itemId);

        if (itemRow) {
            this.workspaceTreeStore.setActiveWorkspaceId(
                itemRow.workspaceId,
                true,
            );
            this.workspaceTreeStore.setActiveBoardId(itemRow.boardId, true);
            this.workspaceTreeStore.setActiveItemCategoryId(
                itemRow.itemCategoryId,
                true,
            );
            this.workspaceTreeStore.setActiveItemId(itemId, true);
        }

        if (itemData) {
            this.setLocateClassName("item", itemData);
        }
        this.redirectToActiveItem();
        this.workspaceStore.setIsFetchingItem(false);
    }

    /**
     * Navigates to a board with full business logic
     * @param {number} boardId - The board ID to navigate to
     * @param {number} spaceId - The space ID containing the board
     * @param {Object} boardData - The board data object for animation
     */
    async navigateToBoard(boardId, spaceId, boardData = null) {
        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        try {
            this.appStateStore.startRestoringState();
            await this.workspaceTreeStore.loadBoards(space);
            const board = this.workspaceTreeStore.getBoardById(boardId);
            this.workspaceTreeStore.setActiveBoardId(board?.id, true);
            this.workspaceStore.openBoardsPanel();

            if (boardData) {
                this.handleLocationAnimation("board", boardData);
            } else {
                this.handleLocationAnimation("board", board);
            }

            this.workspaceStore.setIsFetchingItem(false);
            this.appStateStore.stopRestoringState();
        } catch (error) {
            this.workspaceStore.setIsFetchingItem(false);
            this.appStateStore.stopRestoringState();
        }
    }

    /**
     * Navigates to a space with full business logic
     * @param {number} spaceId - The space ID to navigate to
     * @param {Object} spaceData - The space data object for animation
     */
    async navigateToSpace(spaceId, spaceData = null) {
        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, true);
        this.workspaceStore.openSpacesPanel();

        if (spaceData) {
            this.handleLocationAnimation("space", spaceData);
        } else {
            this.handleLocationAnimation("space", space);
        }

        this.workspaceStore.setIsFetchingItem(false);
    }

    /**
     * Navigates to an item group with full business logic
     * @param {number} groupId - The item group ID to navigate to
     * @param {number} boardId - The board ID containing the group
     * @param {number} spaceId - The space ID containing the board
     * @param {Object} groupData - The group data object for animation
     */
    async navigateToItemGroup(groupId, boardId, spaceId, groupData = null) {
        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        this.appStateStore.startRestoringState();
        await this.workspaceTreeStore.loadBoards(space);
        const board = this.workspaceTreeStore.getBoardById(boardId);
        this.workspaceTreeStore.setActiveBoardId(board?.id, false);

        const boardRow = this.workspaceTreeStore.getBoardById(board.id);
        if (!boardRow?.itemCategoryOrderedIds?.length) {
            await this.workspaceTreeStore.loadItems(space, board);
        }

        const data = this.workspaceTreeStore.getItemCategoryById(groupId);
        this.workspaceTreeStore.setActiveItemId(null, false);
        this.workspaceTreeStore.setActiveItemCategoryId(groupId, true);
        this.workspaceStore.openItemsPanel();

        if (groupData) {
            this.handleLocationAnimation("group", groupData, {
                groupType: "item",
            });
        } else {
            this.handleLocationAnimation("group", data, { groupType: "item" });
        }

        this.workspaceStore.setIsFetchingItem(false);
        this.appStateStore.stopRestoringState();
    }

    /**
     * Navigates to a board group with full business logic
     * @param {number} groupId - The board group ID to navigate to
     * @param {number} spaceId - The space ID containing the group
     * @param {Object} groupData - The group data object for animation
     */
    async navigateToBoardGroup(groupId, spaceId, groupData = null) {
        this.appStateStore.is_dashboard_mode = false;
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        const space = this.workspaceTreeStore.getWorkspaceById(spaceId);
        this.workspaceTreeStore.setActiveWorkspaceId(space?.id, false);

        this.appStateStore.startRestoringState();
        await this.workspaceTreeStore.loadBoards(space);

        const data = this.workspaceTreeStore.getBoardCategoryById(groupId);

        this.workspaceTreeStore.setActiveBoardId(null, false);
        this.workspaceTreeStore.setActiveBoardCategoryId(groupId, true);
        this.workspaceStore.openBoardsPanel();

        if (groupData) {
            this.handleLocationAnimation("group", groupData, {
                groupType: "board",
            });
        } else {
            this.handleLocationAnimation("group", data, { groupType: "board" });
        }

        this.workspaceStore.setIsFetchingItem(false);
        this.appStateStore.stopRestoringState();
    }

    /**
     * Navigates to a space group with full business logic
     * @param {number} groupId - The space group ID to navigate to
     * @param {Object} groupData - The group data object for animation
     */
    async navigateToSpaceGroup(groupId, groupData = null) {
        this.appStateStore.is_dashboard_mode = false;
        this.appStateStore.setWorkAsSelectedModule();
        // Routing handled by EntityNavigator or calling context
        this.workspaceStore.setIsFetchingItem(true);

        this.appStateStore.startRestoringState();
        const data = this.workspaceTreeStore.getWorkspaceCategoryById(groupId);
        this.workspaceTreeStore.setActiveWorkspaceCategoryId(groupId, false);
        this.workspaceTreeStore.setActiveWorkspaceId(null, true);
        this.workspaceStore.openSpacesPanel();

        if (groupData) {
            this.handleLocationAnimation("group", groupData, {
                groupType: "space",
            });
        } else {
            this.handleLocationAnimation("group", data, { groupType: "space" });
        }

        this.workspaceStore.setIsFetchingItem(false);
        this.appStateStore.stopRestoringState();
    }

    /**
     * Generic navigation method that can be used by any caller
     * @param {string} type - The type of entity to navigate to
     * @param {Object} params - Parameters object containing required data
     * @param {Function} openConversationMappedItem - Optional callback for opening conversation mapped item
     */
    async navigateToEntity(type, params, openConversationMappedItem = null) {
        this.workspaceStore.active_item_locator_cancel = false;

        const navigationMap = {
            item: () =>
                this.navigateToItem(
                    params.itemId,
                    params.spaceId,
                    params.itemData,
                    openConversationMappedItem,
                ),
            board: () =>
                this.navigateToBoard(
                    params.boardId,
                    params.spaceId,
                    params.boardData,
                ),
            space: () => this.navigateToSpace(params.spaceId, params.spaceData),
            "item-group": () =>
                this.navigateToItemGroup(
                    params.groupId,
                    params.boardId,
                    params.spaceId,
                    params.groupData,
                ),
            "board-group": () =>
                this.navigateToBoardGroup(
                    params.groupId,
                    params.spaceId,
                    params.groupData,
                ),
            "space-group": () =>
                this.navigateToSpaceGroup(params.groupId, params.groupData),
        };

        const navigationFunction = navigationMap[type];
        if (navigationFunction) {
            await navigationFunction();
        } else {
            console.warn(`Unknown navigation type: ${type}`);
        }
    }

    /**
     * Handles location animation for different entity types
     * @param {string} type - The type of entity (item, board, space, group)
     * @param {Object} entity - The entity object
     * @param {Object} additionalData - Additional data for group types
     */
    handleLocationAnimation(type, entity, additionalData = null) {
        const animationMap = {
            item: () => {
                this.setLocateClassName("item", entity);
                this.redirectToActiveItem();
            },
            board: () => {
                this.setLocateClassName("board", entity);
                this.redirectToActiveBoard();
            },
            space: () => {
                this.setLocateClassName("space", entity);
                this.redirectToActiveSpace();
            },
            group: () => {
                this.setLocateClassName("group", entity);
                if (additionalData?.groupType === "board") {
                    this.redirectToActiveBoardGroup();
                } else if (additionalData?.groupType === "space") {
                    this.redirectToActiveSpaceGroup();
                } else if (additionalData?.groupType === "item") {
                    this.redirectToActiveItem();
                }
            },
        };

        const animationFunction = animationMap[type];
        if (animationFunction) {
            animationFunction();
        } else {
            console.warn(`Unknown location animation type: ${type}`);
        }
    }
}

/**
 * Factory function to create a LocationAnimation instance
 * @returns {LocationAnimation} - New LocationAnimation instance
 */
export function createLocationAnimation() {
    return new LocationAnimation();
}

export default LocationAnimation;
